import { PlusIcon } from '@heroicons/react/24/outline';
import {
  CheckIcon,
  ChevronDownIcon,
  XMarkIcon,
} from '@heroicons/react/16/solid';
import { useTranslation } from 'react-i18next';
import TogglePresence from './TogglePresence';
import { handleUserFullName } from '@/functions/handleUserFullName';
import Enrollment from '@/models/Enrollment';
import Klass from '@/models/Klass';
import ScheduledLesson from '@/models/ScheduledLesson';
import ScheduledLessonReport from '@/models/ScheduledLessonReport';
import { Badge, Button, Loading, Table } from '../ui/daisy';
import ConditionalRenderer from '../ui/custom/ConditionalRenderer';
import { twMerge } from 'tailwind-merge';
import StudentOccurrences from './occurrences/StudentOccurrences';
import usePresenceHandler from '@/data/hook/scheduled-lesson-report/usePresenceHandler';
import useStudentReportRowPermissions from '@/data/hook/scheduled-lesson-report/useStudentReportRowPermissions';
import useStudentReportOcurrences from '@/data/hook/scheduled-lesson-report/useStudentReportOcurrences';
import { ReportOverlay } from './ReportOverlay';

type StudentReportProps = {
  enrollment: Enrollment;
  report?: ScheduledLessonReport;
  handleEnrollment?: (enrollment: Enrollment) => void;
  scheduledLesson: ScheduledLesson;
  klass: Klass;
};

export function StudentReportRow({
  enrollment,
  report,
  handleEnrollment,
  scheduledLesson,
  klass,
}: StudentReportProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'scheduledLessonReportModal',
  });

  const { occurrences, isLoading, error, invalidate, toggle } =
    useStudentReportOcurrences({
      enrollment,
      scheduledLesson,
    });

  const { loading, handlePresence } = usePresenceHandler({
    enrollment,
    report,
    scheduledLesson,
    handleEnrollment,
  });

  const { permitions } = useStudentReportRowPermissions({
    scheduledLesson,
    klass,
    report,
    enrollment,
  });

  const togglePresenceDisabled =
    !permitions.hasPresencePermission || !permitions.isReportEditAllowed;

  const name = handleUserFullName(enrollment.student);

  const overlayCondition = !!report && !permitions.canEditReportPanel;

  return (
    <>
      <Table.Row>
        <Table.Cell>
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={toggle.toggleRow}>
              <ChevronDownIcon
                className={twMerge('size-4', toggle.isOpen ? 'rotate-180' : '')}
              />
            </Button>
            {name}
          </div>
        </Table.Cell>

        <Table.Cell>
          <Button
            variant="soft"
            color="primary"
            size="sm"
            onClick={toggle.openOccurrenceForm}
            disabled={
              toggle.isCreatingOccurrence || !permitions.hasReportPermission
            }
          >
            <PlusIcon className="size-3" />
            {t('columns.ocurrence')}
            {occurrences.length > 0 && (
              <Badge
                color="primary"
                className="rounded-full"
                size="xs"
              >
                {occurrences.length}
              </Badge>
            )}
          </Button>
        </Table.Cell>

        <Table.Cell>
          <div className="flex items-center justify-center gap-2">
            <TogglePresence
              key={`presenceCheckbox${report?.id}`}
              presence={report?.presence}
              onClick={handlePresence}
              loading={loading}
              disabled={togglePresenceDisabled}
              checkedIcon={
                <CheckIcon className="border text-success rounded-full z-0" />
              }
              uncheckedIcon={<XMarkIcon />}
            />
          </div>
        </Table.Cell>
      </Table.Row>

      <ConditionalRenderer condition={toggle.isOpen}>
        <Table.Row>
          <Table.Cell colSpan={3} className="relative">
            <ConditionalRenderer
              condition={!loading}
              fallback={
                <div className="flex items-center justify-center grow">
                  <Loading color="primary" size="sm" />
                </div>
              }
            >
              <ReportOverlay condition={overlayCondition} />
              <StudentOccurrences
                occurrences={occurrences}
                studentId={enrollment.student.id}
                isLoading={isLoading}
                error={error}
                invalidate={invalidate}
                scheduledLessonId={scheduledLesson.id}
                klassId={klass.id}
                canEditReport={permitions.isReportEditAllowed}
                canManage={permitions.hasReportPermission}
                isCreatingOccurrence={toggle.isCreatingOccurrence}
                onCloseOccurrenceForm={toggle.closeOccurrenceForm}
                onCancelOccurrenceForm={toggle.cancelOccurrenceForm}
                toggleRow={toggle.toggleRow}
              />
            </ConditionalRenderer>
          </Table.Cell>
        </Table.Row>
      </ConditionalRenderer>
    </>
  );
}
